'use client';

import React from 'react';
import { Box, Stack, Text, Button, Field, Input, CloseButton, Switch, Alert, Dialog, Portal } from '@chakra-ui/react';
import { InputGroup } from '@/components/ui';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { signupStyles } from '../../signup-templates/signup.styles';
import { PaymentProps } from '../../templates/clinic/types/template.types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { paymentSchema, TPayment } from '../payment/payment.validator';
import { useAuthState } from '@/app/_providers/store';
import { createPaymentIntent, paySubscription } from '@/services/payment/payment.service';
import BarLoader from '@/components/ui/loaders/BarLoader';
import Image from 'next/image';
import useSubscriptionPrice from '@/hooks/product/useSubscriptionPrice';
import { getMe } from '@/services/user/user.service';
import useAuthFunc from '@/hooks/auth/useAuthFunc';
import { discountCouponRedemption } from '@/services/discount/discount.service';

const ClinicPartnerPayment = ({ previous, packageSelected }: PaymentProps) => {
   const [discounted, setDiscounted] = React.useState(false);
   const { handleLogout } = useAuthFunc();
   const { product, price, setPrice } = useSubscriptionPrice(packageSelected as string);
   const [paymentSuccess, setPaymentSuccess] = React.useState(false);
   const [discountValue, setDiscountValue] = React.useState(0);
   const { user } = useAuthState();
   const stripe = useStripe();
   const elements = useElements();

   const {
      watch,
      setValue,
      register,
      handleSubmit,
      setError,
      formState: { errors, isSubmitting },
   } = useForm<TPayment>({ defaultValues: { auto_renew: false }, resolver: zodResolver(paymentSchema) });
   const [isCompleted, setIsCompleted] = React.useState(false);
   const { discount_code, auto_renew } = watch();

   const onSubmit = handleSubmit(async values => {
      if (!elements || !stripe || !product) {
         return;
      }

      // // Create the PaymentIntent and obtain clientSecret from your server endpoint
      const [intentError, paymentIntentData] = await createPaymentIntent({
         email: user?.email as string,
         name: `${user?.first_name} ${user?.last_name}`,
         method: 'card',
         amount: (product.priceDetails.amount / 100) as number,
         currency: 'gbp',
         auto_renew: values.auto_renew,
         couponCode: values.discount_code,
         reason: 'new',
      });

      if (intentError) {
         console.log(intentError);
         setError('root', { message: intentError.response?.data.message as string, type: 'manual' });
         return;
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
         setError('root', { message: 'Card input not ready.', type: 'manual' });
         return;
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
         paymentIntentData.response.clientSecret,
         {
            payment_method: {
               card: cardElement,
               billing_details: {
                  email: user?.email as string,
                  name: `${user?.first_name} ${user?.last_name}`,
               },
            },
         },
      );

      if (stripeError) {
         console.log(stripeError);
         setError('root', { message: stripeError.message as string, type: 'manual' });
         return;
      }

      if (paymentIntent.status === 'succeeded') {
         setPaymentSuccess(true);
      }

      const [error] = await paySubscription({
         couponCode: values.discount_code as string,
         priceId: product.priceDetails.priceId,
         paymentMethod: paymentIntent?.payment_method as string,
         packageId: product._id as string,
      });

      if (error) {
         console.log(error);
         setError('root', { message: error.response?.data.message as string, type: 'manual' });
         return;
      }

      const [getMeError, data] = await getMe();

      if (getMeError) {
         console.log(getMeError);
      }

      if (data?.response?.registration_step === 'COMPLETED') {
         setIsCompleted(true);
         setTimeout(() => {
            handleLogout();
         }, 5000);
      }
   });

   const onCouponRedemption = async () => {
      if (!discount_code || errors.discount_code) return;

      const [error, data] = await discountCouponRedemption(discount_code as string);

      if (error) {
         console.log(error);
         setError('root', { message: error.response?.data.message as string, type: 'manual' });
         return;
      }

      if (data.response.amount_type === 'percentage') {
         setPrice((price as number) * (1 - data.response.amount / 100));
         const discountValues = (price as number) * (data.response.amount / 100);
         setDiscountValue(data.response.amount - discountValues);
      }
      if (data.response.amount_type === 'fixed') {
         setPrice((price as number) - data.response.amount);
         setDiscountValue(data.response.amount);
      }

      setDiscounted(true);
   };

   const endElement = discounted ? (
      <CloseButton
         size="md"
         color="brand"
         bg="transparent"
         onClick={() => {
            setValue('discount_code', '');
            setDiscounted(false);
            setPrice((product?.priceDetails.amount as number) / 100 || 29);
            setDiscountValue(0);
         }}
         me="-2"
      />
   ) : (
      <Button
         width="70px"
         height="30px"
         fontSize={'xs'}
         fontWeight="semibold"
         onClick={onCouponRedemption}
         disabled={!discount_code}
      >
         Apply
      </Button>
   );

   if (isCompleted) {
      return (
         <Box display="flex" flexDirection="column" alignItems="center" gap="10px" h="100%" justifyContent="center">
            <Box
               style={{
                  height: '80px',
                  width: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#F5E876',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
               }}
            >
               <img src="/assets/images/tick.png" alt="check image" />
            </Box>
            <Text fontWeight="bold" fontSize="6xl" textAlign="center">
               APPLICATION SUCCESSFULLY SUBMITTED
            </Text>
            <Box textAlign="center">
               <Text as="p">You have successfully applied as a Clinic/Partner.</Text>
               <Text as="p">We will notify you whenever it’s ready.</Text>
            </Box>
            <Button width="150px" mt="10px" height="40px" onClick={handleLogout}>
               Ok
            </Button>
         </Box>
      );
   }

   return (
      <Box as="form" css={signupStyles.signup_form} onSubmit={onSubmit}>
         {isSubmitting && <BarLoader loadingText="Processing payment..." />}
         <Stack gap="4" align="flex-start" minW="full">
            {errors.root?.message && (
               <Alert.Root status="error">
                  <Alert.Indicator />
                  <Alert.Title>{errors.root.message}</Alert.Title>
               </Alert.Root>
            )}
            <Field.Root>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Discount code
               </Field.Label>
               <InputGroup endElement={endElement} style={{ width: '100%' }}>
                  <Input
                     placeholder="Discount Code"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     {...register('discount_code')}
                  />
               </InputGroup>
            </Field.Root>
            <Box css={signupStyles.payment_details}>
               <Box css={signupStyles.payment_details_con}>
                  <Text textStyle="xs" color="white">
                     Cancard verification and the card
                  </Text>
                  <Text textStyle="xs" color="white">
                     {/* £{price || 29} */} £{(product?.priceDetails?.amount as number) / 100}
                  </Text>
               </Box>
               <Box css={signupStyles.payment_details_con}>
                  <Text textStyle="xs" color="white">
                     Discount
                  </Text>
                  <Text textStyle="xs" color="white">
                     {/* -£0.00 */} £{`${discountValue}`}
                  </Text>
               </Box>
               <Box css={signupStyles.payment_details_total}>
                  <Text textStyle="xs" color="white">
                     Total Amount
                  </Text>
                  <Text textStyle="xs" color="white">
                     £{price}
                  </Text>
               </Box>
            </Box>
            <Field.Root>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Card number
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>

               {/* Move CardElement inside this container */}
               <Box css={signupStyles.card_details}>
                  <CardElement
                     options={{
                        style: {
                           base: {
                              fontSize: '16px',
                              color: '#fff',
                              '::placeholder': {
                                 color: '#bbb',
                              },
                           },
                           invalid: {
                              color: '#e5424d',
                           },
                        },
                     }}
                  />
               </Box>
            </Field.Root>

            <Box css={signupStyles.auto_renewal}>
               <Box>
                  <Text textStyle="xs" color="white" fontWeight="bold">
                     Auto-Renewal
                  </Text>
                  <Text textStyle="xs" color="white">
                     Pay the next renewal automatically
                  </Text>
               </Box>
               <Box>
                  <Switch.Root
                     name="auto_renew"
                     checked={auto_renew}
                     onCheckedChange={({ checked }) => {
                        setValue('auto_renew', checked);
                     }}
                  >
                     <Switch.Label textStyle="xs" color="white" w="26px">
                        {auto_renew ? 'ON' : 'OFF'}
                     </Switch.Label>
                     <Switch.HiddenInput />
                     <Switch.Control bg={auto_renew ? 'brand' : 'darkGrey'} color={auto_renew ? 'white' : 'white'} />
                  </Switch.Root>
               </Box>
            </Box>
            <Box css={signupStyles.signup_btn_con}>
               <Button onClick={previous} bg="darkerBtn" textStyle="xs" color="white" h="10" maxW="36" minW="36" p="0">
                  Back
               </Button>
               <Button type="submit" disabled={isSubmitting} textStyle="xs" h="10" maxW="36" minW="36" p="0">
                  Pay Now
               </Button>
            </Box>
         </Stack>
         <Dialog.Root open={paymentSuccess} lazyMount placement="center" motionPreset="slide-in-bottom">
            <Portal>
               <Dialog.Backdrop />
               <Dialog.Positioner>
                  <Dialog.Content bg="#202020" rounded="15px" py="4" maxWidth="340px">
                     <Dialog.Body>
                        <Box
                           w="16"
                           h="16"
                           rounded="50%"
                           bg="#f5e87614"
                           m="0 auto 10px"
                           display="flex"
                           alignItems="center"
                           justifyContent="center"
                        >
                           <Image src="/assets/gif/payment_success.gif" alt="" width="30" height="30" />
                        </Box>
                        <Dialog.Title color="white" textStyle="xl" textAlign="center" mb="2">
                           Payment successfull
                        </Dialog.Title>
                        <Text color="darkGray" textStyle="sm" textAlign="center">
                           We’ve sent you an email with the necessary instructions to continue
                        </Text>
                     </Dialog.Body>
                     <Dialog.Footer justifyContent="center" pt="0">
                        <Dialog.ActionTrigger asChild>
                           <Button variant="outline" h="10" onClick={() => {}}>
                              OK
                           </Button>
                        </Dialog.ActionTrigger>
                     </Dialog.Footer>
                  </Dialog.Content>
               </Dialog.Positioner>
            </Portal>
         </Dialog.Root>
      </Box>
   );
};

export default ClinicPartnerPayment;
